import { NgModule } from "@angular/core";
import { RouterModule, Routes } from "@angular/router";
import { AuthGuard } from "./core/services/guards/auth.guard";
import { loginOidcHandoffGuard } from "./core/services/guards/login-handoff.guard";
import { StudentAuthGuard } from "./core/services/guards/student.guard";
import { RoleBasedPreloadingStrategy } from "./core/services/guards/role-based-preloading.strategy";
import { SocketService } from "./core/http/socket/socket.service";

/**
 * The application's route table.
 *
 * Exported so `app-routing.module.vitest.spec.ts` asserts against the real table rather than
 * against hand-copied literals, which would pass whatever the router actually said.
 */
export const APP_ROUTES: Routes = [
  {
    path: "login",
    canMatch: [loginOidcHandoffGuard],
    // `SocketService` was provided by the lazy LoginModule this route used to load. It stays a
    // route provider rather than moving to root: `AuthSession` resolves the instance the
    // sign-in form renders against, and a root singleton would be a different one.
    providers: [SocketService],
    loadComponent: () =>
      import("./core/componets/login/login.component").then((m) => m.LoginComponent)
  },

  {
    path: "oidc-callback",
    loadComponent: () =>
      import("./features/public-entry/oidc-callback/oidc-callback").then((m) => m.OidcCallback)
  },

  {
    // Straight to the redesigned screen: the wrapper component that used to sit here rendered
    // nothing but this and carried a dead copy of the reset logic.
    path: "forgot-password",
    loadComponent: () =>
      import("./features/auth/forgot-password/forgot-password").then((m) => m.ForgotPassword)
  },

  {
    path: "payment-success",
    loadComponent: () =>
      import("./features/public-entry/payment-receipt/payment-receipt").then((m) => m.PaymentReceipt)
  },
  {
    path: "terms",
    loadComponent: () =>
      import("./features/public-entry/terms/terms-view").then((m) => m.TermsView)
  },
  // `:id2` is the tenant domain the mobile apps append; nothing reads it, but the two-segment
  // form is kept because links already in inboxes use it.
  {
    path: "activate/:id",
    loadComponent: () =>
      import("./features/public-entry/set-password/set-password").then((m) => m.SetPassword)
  },
  {
    path: "activate/:id/:id2",
    loadComponent: () =>
      import("./features/public-entry/set-password/set-password").then((m) => m.SetPassword)
  },
  {
    path: "onboarding",
    loadChildren: () =>
      import("./features/onboarding/onboarding.routes").then((m) => m.ONBOARDING_ROUTES)
  },
  // `verify/sender` must stay above `verify/:token`, or the literal is swallowed by the parameter.
  {
    path: "verify/sender",
    loadComponent: () =>
      import("./features/public-entry/verify-sender/verify-sender").then((m) => m.VerifySender)
  },
  {
    path: "verify/:token",
    loadComponent: () =>
      import("./features/public-entry/verify-account/verify-account").then((m) => m.VerifyAccount)
  },

  {
    path: "register/:id",
    loadChildren: () =>
      import("./features/registration/registration.routes").then((m) => m.REGISTRATION_ROUTES)
  },

  {
    path: "enquiry/:type/:id",
    loadComponent: () =>
      import("./features/enquiry/form/enquiry-form").then((m) => m.EnquiryFormScreen)
  },
  {
    path: "checkout/:id",
    loadComponent: () =>
      import("./features/public-funnel/link-payment/link-payment").then((m) => m.LinkPayment)
  },
  {
    path: "share/:id/:type/:center/:url",
    loadComponent: () =>
      import("./features/public-funnel/share/course-share").then((m) => m.CourseShare)
  },
  {
    // Facebook's login redirect target, registered in the Meta app. No guard: it only relays the
    // token to the window that opened the popup.
    path: "meta/callback",
    loadComponent: () =>
      import("./features/meta/callback/meta-callback").then((m) => m.MetaCallback)
  },
  {
    path: "meet",
    loadComponent: () =>
      import("./features/meet/join/meeting-join").then((m) => m.MeetingJoin)
  },

  {
    path: "manage",
    loadChildren: () => import("./features/manage/manage.routes").then((m) => m.MANAGE_ROUTES),
    data: { role: "admin" }
  },
  {
    path: "learn",
    loadChildren: () => import("./features/learn/learn.routes").then((m) => m.LEARN_ROUTES),
    data: { role: "student" }
  },
  // `RoleBasedPreloadingStrategy` has no case for the `parent` role, so this chunk loads on
  // navigation rather than being pushed at every signed-in account. Entry is decided by
  // `parentAuthGuard`, which asks the server who this account observes.
  {
    path: "parent",
    loadChildren: () => import("./features/parent/parent.routes").then((m) => m.PARENT_ROUTES),
    data: { role: "parent" }
  },
  {
    path: "platform",
    loadChildren: () => import("./features/platform/platform.routes").then((m) => m.PLATFORM_ROUTES)
  },
  // A workspace of one, rendered without the shell — the design files it beside the auth surface
  // rather than inside a persona, because there is no institution to put chrome around.
  {
    path: "solo",
    loadChildren: () => import("./features/solo/solo.routes").then((m) => m.SOLO_ROUTES)
  },

  { path: "user", redirectTo: "manage", pathMatch: "prefix" },
  { path: "students-portal", redirectTo: "learn", pathMatch: "prefix" },

  { path: "settings/:sub", redirectTo: "manage/setting/:sub" },
  { path: "account-settings/:sub", redirectTo: "manage/setting/:sub" },

  {
    path: "share-payment/:id",
    loadComponent: () =>
      import("./features/public-funnel/checkout/share-checkout").then((m) => m.ShareCheckout)
  },
  { path: "", redirectTo: "/login", pathMatch: "full" },

  {
    path: "invoice/:id",
    loadComponent: () =>
      import("./features/manage/invoices/order-invoice/order-invoice").then((m) => m.OrderInvoiceDoc),
    canActivate: [AuthGuard],
    data: { role: "admin" }
  },

  {
    path: "invoice/student/:id",
    loadComponent: () =>
      import("./features/learn/orders/invoice/learn-order-invoice").then((m) => m.LearnOrderInvoice),
    canActivate: [StudentAuthGuard],
    data: { role: "student" }
  },
  {
    path: "pay/:id",
    loadComponent: () =>
      import("./features/manage/invoices/royalty-invoice/royalty-invoice").then(
        (m) => m.RoyaltyInvoiceDoc
      ),
    canActivate: [AuthGuard],
    data: { role: "admin" }
  },

  {
    path: "server-down",
    loadComponent: () =>
      import("./features/public-entry/server-down/server-down").then((m) => m.ServerDown)
  },

  { path: "view-terms-conditions", redirectTo: "terms", pathMatch: "full" },
  { path: "account/activate/:id", redirectTo: "activate/:id" },
  { path: "account/activate/:id/:id2", redirectTo: "activate/:id/:id2" },
  { path: "student-register-form/:id", redirectTo: "register/:id" },
  { path: "enquiry-form/:type/:id", redirectTo: "enquiry/:type/:id" },
  { path: "stripe-payment/:id", redirectTo: "checkout/:id" },
  { path: "course-share/:id/:type/:center/:url", redirectTo: "share/:id/:type/:center/:url" },
  { path: "share-course-payment/:id", redirectTo: "share-payment/:id" },
  { path: "order-invoice/:id", redirectTo: "invoice/:id" },
  { path: "order-invoice-student/:id", redirectTo: "invoice/student/:id" },
  { path: "pay-invoice/:id", redirectTo: "pay/:id" },
  // RP-initiated logout sometimes lands the OIDC end-session redirect on the portal host
  // (white-label /auth ingress edge). Without a matching route Angular throws NG04002.
  { path: "auth/session/end", redirectTo: "/login", pathMatch: "full" }
];

@NgModule({
  imports: [RouterModule.forRoot(APP_ROUTES, { preloadingStrategy: RoleBasedPreloadingStrategy })],
  exports: [RouterModule]
})
export class AppRoutingModule { }
